"use client";

import { Html, SmartImage, SmartLink } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { FeaturesSectionProps } from "@/Templates/SectionsProps";
import { Icon } from "@iconify/react";
import { FC, ReactElement, ReactNode } from "react";
import HomeOneReasonsSectionGenerated, {
  HomeOneReasonsSectionGeneratedProps,
} from "../Sections/_generated/HomeOneReasonsSection.generated";
import HomeSeventeenWellnessSectionGenerated, {
  HomeSeventeenWellnessSectionGeneratedProps,
} from "../Sections/_generated/HomeSeventeenWellnessSection.generated";
import HomeTwelveTestPlanSectionGenerated, {
  HomeTwelveTestPlanSectionGeneratedProps,
} from "../Sections/_generated/HomeTwelveTestPlanSection.generated";
import {
  buildGeneratedSectionProps,
  renderGeneratedSection,
} from "./generated-section-renderer";

type GeneratedFeaturesRenderer = (props: FeaturesSectionProps) => ReactElement;
type GeneratedFeaturesProps = Record<string, unknown>;
type GeneratedFeaturesItem = Record<string, unknown>;

const FeaturesAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings
    .SiteTemplateFeaturesAppearance;

const titleKeyMatchers = [/H20H2Text$/, /H30H3Text$/];
const titleDecorationKeyMatchers = [/H20Span\d+SpanText$/, /H20H2Text2$/];
const subtitleKeyMatchers = [/DivText$/];
const descriptionKeyMatchers = [/P0PText$/, /p0Text$/];

function buildGeneratedFeaturesProps<T extends GeneratedFeaturesProps>(
  sectionKey: string,
  overrides?: Partial<T>,
): T {
  return buildGeneratedSectionProps<T>(sectionKey, overrides);
}

function findFirstMatchingKey(
  target: GeneratedFeaturesProps | GeneratedFeaturesItem,
  matchers: RegExp[],
): string | undefined {
  return Object.keys(target).find((key) =>
    matchers.some((matcher) => matcher.test(key)),
  );
}

function setFirstMatchingValue(
  target: GeneratedFeaturesProps | GeneratedFeaturesItem,
  matchers: RegExp[],
  value: unknown,
): void {
  if (value === null || value === undefined || value === "") {
    return;
  }

  const key = findFirstMatchingKey(target, matchers);
  if (!key) {
    return;
  }

  target[key] = value;
}

function setAllMatchingValues(
  target: GeneratedFeaturesProps | GeneratedFeaturesItem,
  matchers: RegExp[],
  value: unknown,
): void {
  for (const key of Object.keys(target)) {
    if (matchers.some((matcher) => matcher.test(key))) {
      target[key] = value;
    }
  }
}

function isImageLikeValue(value?: string | null): boolean {
  if (!value) {
    return false;
  }

  return (
    value.startsWith("/") ||
    value.startsWith("http://") ||
    value.startsWith("https://")
  );
}

function renderIconNode(
  icon?: string | null,
  className?: string,
): ReactElement | null {
  if (!icon) {
    return null;
  }

  if (isImageLikeValue(icon)) {
    return <SmartImage src={icon} className={className} alt="" />;
  }

  if (icon.includes(":")) {
    return <Icon icon={icon} className={className} />;
  }

  return <i className={className ? `${icon} ${className}` : icon} />;
}

function renderPlainTitle(
  title: string | undefined,
  className: string,
  defaultNode: ReactNode,
): ReactNode {
  if (!title) {
    return defaultNode;
  }

  return className ? <h2 className={className}>{title}</h2> : <h2>{title}</h2>;
}

function toTwoDigitNumber(value: number): string {
  return value.toString().padStart(2, "0");
}

function applyFeaturesSectionOverrides<T extends GeneratedFeaturesProps>(
  generatedProps: T,
  props: FeaturesSectionProps,
): T {
  const nextProps: GeneratedFeaturesProps = {
    ...generatedProps,
  };
  const descriptionNode = props.setting.Description ? (
    <Html value={props.setting.Description} />
  ) : undefined;

  setFirstMatchingValue(nextProps, titleKeyMatchers, props.setting.Title);
  if (props.setting.Title) {
    setAllMatchingValues(nextProps, titleDecorationKeyMatchers, "");
  }
  setFirstMatchingValue(nextProps, subtitleKeyMatchers, descriptionNode);
  setFirstMatchingValue(nextProps, descriptionKeyMatchers, descriptionNode);

  return nextProps as T;
}

function applyHomeOneItems(
  generatedProps: HomeOneReasonsSectionGeneratedProps,
  props: FeaturesSectionProps,
): HomeOneReasonsSectionGeneratedProps {
  const nextProps = applyFeaturesSectionOverrides(generatedProps, props);
  const defaults = nextProps.reasonsSection46s;

  return {
    ...nextProps,
    reasonsSection46s: (props.lists.Default ?? []).map((listItem, index) => ({
      ...(defaults[index % defaults.length] ?? {}),
      key: listItem.Id,
      h30Text: listItem.Title,
      p0Text: listItem.Description ? (
        <Html value={listItem.Description} />
      ) : undefined,
      i0ClassName:
        !listItem.Icon.includes(":") && !isImageLikeValue(listItem.Icon)
          ? listItem.Icon
          : (defaults[index % defaults.length]?.i0ClassName ?? ""),
    })),
  };
}

function applyHomeTwelveItems(
  generatedProps: HomeTwelveTestPlanSectionGeneratedProps,
  props: FeaturesSectionProps,
): HomeTwelveTestPlanSectionGeneratedProps {
  const nextProps = applyFeaturesSectionOverrides(generatedProps, props);
  const defaults = nextProps.testPlanSection4s;

  return {
    ...nextProps,
    testPlanSection4s: (props.lists.Default ?? []).map((listItem, index) => ({
      ...(defaults[index % defaults.length] ?? {}),
      key: listItem.Id,
      h30Text: listItem.Title,
      p0Text: listItem.Description ? (
        <Html value={listItem.Description} />
      ) : undefined,
      href: listItem.Link || "#",
    })),
  };
}

function applyHomeSeventeenItems(
  generatedProps: HomeSeventeenWellnessSectionGeneratedProps,
  props: FeaturesSectionProps,
): HomeSeventeenWellnessSectionGeneratedProps {
  const nextProps = applyFeaturesSectionOverrides(generatedProps, props);
  const defaults = nextProps.wellnessSection366FadeInDowns;

  return {
    ...nextProps,
    wellnessSection366FadeInDowns: (props.lists.Default ?? []).map(
      (listItem, index) => ({
        ...(defaults[index % defaults.length] ?? {}),
        key: listItem.Id,
        span0Text: toTwoDigitNumber(index + 1),
        h30Text: listItem.Title,
        span0Span0Text: "",
        p0Text: listItem.Description ? (
          <Html value={listItem.Description} />
        ) : undefined,
      }),
    ),
  };
}

const generatedFeaturesRenderers: Record<string, GeneratedFeaturesRenderer> = {
  [FeaturesAppearance.Home1.key]: (props) => {
    const listItemMap = new Map(
      (props.lists.Default ?? []).map(
        (listItem) => [String(listItem.Id), listItem] as const,
      ),
    );

    return (
      <HomeOneReasonsSectionGenerated
        {...applyHomeOneItems(
          buildGeneratedFeaturesProps<HomeOneReasonsSectionGeneratedProps>(
            "HomeOneReasonsSection",
          ),
          props,
        )}
        sectionDiv0Div0H20H2Renderer={(defaultNode) =>
          renderPlainTitle(props.setting.Title, "section-title", defaultNode)
        }
        reasonsSection46sItemWrapper={(item, content) => {
          const listItem = listItemMap.get(String(item.key));
          const iconNode = renderIconNode(
            listItem?.Icon,
            item.i0ClassName.includes("text-") ? item.i0ClassName : undefined,
          );

          if (!iconNode) {
            return content;
          }

          return (
            <div
              className={item.divClassName ?? "col-lg-4 col-md-6"}
              key={item.key}
            >
              <div
                className={item.div0ClassName ?? "reason-item wow zoomIn"}
                data-wow-duration={item.dataWowDuration ?? "1s"}
              >
                <div className={item.className}>{iconNode}</div>
                <h3 className={item.h30ClassName ?? "custom-title"}>
                  {item.h30Text}
                </h3>
                <p>{item.p0Text}</p>
              </div>
            </div>
          );
        }}
      />
    );
  },
  [FeaturesAppearance.Home12.key]: (props) => {
    const listItemMap = new Map(
      (props.lists.Default ?? []).map(
        (listItem) => [String(listItem.Id), listItem] as const,
      ),
    );

    return (
      <HomeTwelveTestPlanSectionGenerated
        {...applyHomeTwelveItems(
          buildGeneratedFeaturesProps<HomeTwelveTestPlanSectionGeneratedProps>(
            "HomeTwelveTestPlanSection",
          ),
          props,
        )}
        sectionDiv0Div0H20H2Renderer={(defaultNode) =>
          renderPlainTitle(props.setting.Title, "section-title", defaultNode)
        }
        testPlanSection4sItemWrapper={(item) => {
          const listItem = listItemMap.get(String(item.key));
          const iconNode = renderIconNode(
            listItem?.Icon,
            item.imageWithBasePath0ClassName,
          );

          return (
            <div className={item.divClassName ?? "col-lg-4"} key={item.key}>
              <div
                className={item.className}
                data-wow-duration={item.dataWowDuration}
              >
                <div className={item.div0ClassName ?? "plan-content"}>
                  <h3 className={item.h30ClassName ?? "custom-title"}>
                    {item.h30Text}
                  </h3>
                  <p className={item.p0ClassName ?? "description"}>
                    {item.p0Text}
                  </p>
                  <div className={item.div0ClassName2 ?? "view-all"}>
                    <SmartLink
                      href={item.href ?? "#"}
                      className={item.link0ClassName ?? "btn btn-white"}
                    >
                      {item.link0Text}
                      <i
                        className={
                          item.i0ClassName ?? "isax isax-arrow-right-1"
                        }
                      />
                    </SmartLink>
                  </div>
                </div>
                {iconNode ?? (
                  <SmartImage
                    src={item.src}
                    alt={item.alt ?? "icon"}
                    className={item.imageWithBasePath0ClassName}
                  />
                )}
                <SmartImage
                  src={item.imageWithBasePath1Src}
                  alt={item.imageWithBasePath1Alt ?? "icon"}
                  className={
                    item.imageWithBasePath1ClassName ?? "img-fluid plan-img-1"
                  }
                />
              </div>
            </div>
          );
        }}
      />
    );
  },
  [FeaturesAppearance.Home17.key]: (props) => {
    const listItemMap = new Map(
      (props.lists.Default ?? []).map(
        (listItem) => [String(listItem.Id), listItem] as const,
      ),
    );

    return (
      <HomeSeventeenWellnessSectionGenerated
        {...applyHomeSeventeenItems(
          buildGeneratedFeaturesProps<HomeSeventeenWellnessSectionGeneratedProps>(
            "HomeSeventeenWellnessSection",
          ),
          props,
        )}
        sectionDiv0Div0H20H2Renderer={(defaultNode) =>
          renderPlainTitle(
            props.setting.Title,
            "section-title text-gradient",
            defaultNode,
          )
        }
        wellnessSection366FadeInDownsItemWrapper={(item) => {
          const listItem = listItemMap.get(String(item.key));
          const iconNode = renderIconNode(
            listItem?.Icon,
            item.imageWithBasePath0ClassName ?? "img-fluid count-one",
          );

          return (
            <div
              className={
                item.divClassName ??
                "col-lg-3 col-md-6 col-sm-6 d-flex wow fadeInDown"
              }
              data-wow-duration={item.dataWowDuration}
              key={item.key}
            >
              <div className={item.className}>
                <div className={item.div0ClassName ?? "wellness-count"}>
                  <span>{item.span0Text}</span>
                  {iconNode ?? (
                    <SmartImage
                      src={item.src}
                      alt={item.alt ?? "count"}
                      className={
                        item.imageWithBasePath0ClassName ??
                        "img-fluid count-one"
                      }
                    />
                  )}
                </div>
                <div className={item.div1ClassName ?? "wellness-content"}>
                  <h3 className={item.h30ClassName ?? "custom-title"}>
                    {item.h30Text}
                    {item.span0Span0Text ? (
                      <span>{item.span0Span0Text}</span>
                    ) : null}
                  </h3>
                  <p className={item.p0ClassName ?? "description"}>
                    {item.p0Text}
                  </p>
                </div>
                <SmartImage
                  src={item.imageWithBasePath0Src}
                  alt={item.imageWithBasePath0Alt ?? "service"}
                  className={
                    item.imageWithBasePath0ClassName2 ??
                    "img-fluid wellness-img"
                  }
                />
              </div>
            </div>
          );
        }}
      />
    );
  },
};

const GeneratedFeaturesSection: FC<FeaturesSectionProps> = (props) => {
  return renderGeneratedSection(
    props,
    generatedFeaturesRenderers,
    FeaturesAppearance.Home1.key,
    (sectionProps) => sectionProps.setting.Appearance?.Key,
  );
};

export default GeneratedFeaturesSection;
